<template>
  <div class="inventory-page">
    <el-card class="box-card">
      <div slot="header" class="clearfix header-bar">
        <span class="page-title">设备盘点</span>
        <div>
          <el-button icon="el-icon-key" size="small" @click="openTokenInput">设置令牌</el-button>
          <el-button type="primary" icon="el-icon-plus" size="small" @click="openCreate">新建盘点计划</el-button>
        </div>
      </div>

      <el-alert
        type="info"
        :closable="false"
        title="盘点在小程序端扫码进行（后勤/管理员），PC 端负责：新建计划 → 查看盘点结果 → 导出确认单"
        style="margin-bottom: 14px"
      />

      <div class="toolbar">
        <span class="section-title">盘点计划</span>
        <el-radio-group v-model="statusFilter" size="mini" @change="fetchPlans">
          <el-radio-button label="">全部</el-radio-button>
          <el-radio-button label="ongoing">进行中</el-radio-button>
          <el-radio-button label="completed">已完成</el-radio-button>
        </el-radio-group>
      </div>

      <el-table :data="plans" v-loading="loading" border size="small">
        <el-table-column prop="name" label="计划名称" min-width="170" show-overflow-tooltip />
        <el-table-column prop="department" label="盘点科室" width="110" show-overflow-tooltip />
        <el-table-column prop="planned_date" label="计划日期" width="100" align="center" />
        <el-table-column label="状态" width="130" align="center">
          <template slot-scope="scope">
            <el-tag :type="statusTag(scope.row.status)" size="small">{{ statusText(scope.row.status) }}</el-tag>
            <!-- 签字顺序：先结束盘点出结果 → 科室核对 → 再签字；已完成但未签字的持续橙标提醒（追签） -->
            <el-tag
              v-if="scope.row.status === 'completed' && scope.row.signed === false"
              type="warning" size="small" class="unsigned-tag"
            >未签字</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="盘点进度" min-width="150">
          <template slot-scope="scope">
            <div class="progress-cell">
              <span class="progress-num" :class="{ 'progress-done': scope.row.total_count > 0 && scope.row.scanned_count >= scope.row.total_count }">
                {{ scope.row.scanned_count }}/{{ scope.row.total_count }}
              </span>
              <el-progress
                :percentage="percent(scope.row)"
                :stroke-width="8"
                :show-text="false"
                class="progress-bar"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="盘点人" width="90" align="center">
          <template slot-scope="scope">{{ scope.row.planner_name || '-' }}</template>
        </el-table-column>
        <el-table-column label="创建时间" width="140" align="center">
          <template slot-scope="scope">{{ fmtTime(scope.row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="250" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" class="op-btn" @click="openResult(scope.row)">查看结果</el-button>
            <el-button
              v-if="scope.row.status === 'ongoing'"
              type="text" class="op-btn op-warn"
              @click="completePlan(scope.row)"
            >结束盘点</el-button>
            <el-button
              v-if="scope.row.status === 'completed'"
              type="text" class="op-btn"
              @click="reopenPlan(scope.row)"
            >重新开启</el-button>
            <el-button type="text" class="op-btn" @click="downloadReport(scope.row)">确认单</el-button>
            <el-button
              type="text" class="op-btn op-del"
              @click="deletePlan(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 新建盘点计划 -->
    <el-dialog title="新建盘点计划" :visible.sync="createVisible" width="460px" :close-on-click-modal="false">
      <el-form ref="createForm" :model="createForm" :rules="createRules" label-width="90px" size="small">
        <el-form-item label="计划名称" prop="name">
          <el-input v-model="createForm.name" placeholder="如：2026年10月内科病房盘点" maxlength="100" />
        </el-form-item>
        <el-form-item label="盘点科室" prop="department">
          <!-- 换科室下拉后不再需要「须与设备台账完全一致」的提示：下拉源与台账同源 -->
          <department-select v-model="createForm.department" placeholder="请选择盘点科室" />
        </el-form-item>
        <el-form-item label="计划日期" prop="planned_date">
          <el-date-picker
            v-model="createForm.planned_date"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="选择日期"
            style="width: 100%"
          />
        </el-form-item>
      </el-form>
      <div class="create-tip">科室名必须与台账一致（如「内科」「放射科」），否则比对结果会全部算作未盘。</div>
      <div slot="footer">
        <el-button size="small" @click="createVisible = false">取 消</el-button>
        <el-button type="primary" size="small" :loading="creating" @click="submitCreate">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 盘点结果 -->
    <el-dialog
      :title="resultTitle"
      :visible.sync="resultVisible"
      width="900px"
      top="5vh"
      :close-on-click-modal="false"
    >
      <div v-loading="resultLoading">
        <!-- 统计卡片 -->
        <div class="stat-row">
          <div class="stat-item">
            <div class="stat-num">{{ result.total_count || 0 }}</div>
            <div class="stat-label">应盘总数</div>
          </div>
          <div class="stat-item">
            <div class="stat-num">{{ result.scanned_count || 0 }}</div>
            <div class="stat-label">已盘</div>
          </div>
          <div class="stat-item">
            <div class="stat-num stat-danger">{{ result.missing_count || 0 }}</div>
            <div class="stat-label">未盘</div>
          </div>
          <div class="stat-item">
            <div class="stat-num stat-warn">{{ (result.unknown || []).length }}</div>
            <div class="stat-label">盘盈/坏码</div>
          </div>
          <div class="stat-item">
            <div class="stat-num">{{ crossList.length }}</div>
            <div class="stat-label">跨科室发现</div>
          </div>
          <div class="stat-item">
            <div class="stat-num">{{ (result.non_fixed_asset || []).length }}</div>
            <div class="stat-label">非固定资产</div>
          </div>
          <div class="stat-item">
            <div class="stat-num stat-warn">{{ (result.pending || []).length }}</div>
            <div class="stat-label">待核实</div>
          </div>
          <div class="stat-item">
            <div class="stat-num stat-warn">{{ (result.unlabeled || []).length }}</div>
            <div class="stat-label">无码盘盈</div>
          </div>
        </div>

        <div class="result-meta">
          <span>盘点科室：<strong class="hl">{{ result.department || '-' }}</strong></span>
          <span>计划日期：<strong class="hl">{{ result.planned_date || '-' }}</strong></span>
          <span>盘点人：<strong class="hl">{{ result.planner_name || '-' }}</strong></span>
          <span>
            状态：<el-tag :type="statusTag(result.status)" size="mini">{{ statusText(result.status) }}</el-tag>
          </span>
          <span v-if="result.completed_at">完成时间：<strong class="hl">{{ fmtTime(result.completed_at) }}</strong></span>
        </div>

        <!-- 一、未盘设备 -->
        <div class="result-section">
          <div class="result-title">一、未盘设备清单（{{ (result.missing || []).length }} 台）</div>
          <el-table :data="missingWithCross" border size="mini" max-height="260">
            <el-table-column prop="equipment_id" label="设备ID" width="140" />
            <el-table-column prop="name" label="设备名称" min-width="160" show-overflow-tooltip />
            <el-table-column prop="model" label="型号" min-width="120" show-overflow-tooltip />
            <el-table-column prop="status" label="使用状态" width="90" align="center" />
            <el-table-column label="跨科发现" width="130" align="center">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.found_in_department" type="warning" size="mini">
                  发现于{{ scope.row.found_in_department }}
                </el-tag>
                <span v-else>—</span>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 二、非固定资产设备 -->
        <div class="result-section">
          <div class="result-title">二、非固定资产设备（{{ (result.non_fixed_asset || []).length }} 台）</div>
          <el-table :data="result.non_fixed_asset" border size="mini" max-height="200">
            <el-table-column prop="equipment_id" label="设备ID" width="140" />
            <el-table-column prop="name" label="设备名称" min-width="160" show-overflow-tooltip />
            <el-table-column label="扫码时间" width="160" align="center">
              <template slot-scope="scope">{{ fmtTime(scope.row.scanned_at) }}</template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 三、待核实 -->
        <div class="result-section">
          <div class="result-title">三、待核实（{{ (result.pending || []).length }} 条）</div>
          <el-table :data="result.pending" border size="mini" max-height="200">
            <el-table-column prop="equipment_id" label="扫码内容" min-width="220" show-overflow-tooltip />
            <el-table-column label="扫码时间" width="160" align="center">
              <template slot-scope="scope">{{ fmtTime(scope.row.scanned_at) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="70" align="center" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" class="op-btn op-del" @click="handleDeleteDiff(scope.row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 四、盘盈/坏码 -->
        <div class="result-section">
          <div class="result-title">四、盘盈 / 坏码记录（{{ (result.unknown || []).length }} 条）</div>
          <el-table :data="result.unknown" border size="mini" max-height="200">
            <el-table-column prop="equipment_id" label="扫码内容" min-width="200" show-overflow-tooltip />
            <el-table-column label="扫码时间" width="160" align="center">
              <template slot-scope="scope">{{ fmtTime(scope.row.scanned_at) }}</template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 五、无码盘盈设备 -->
        <div class="result-section">
          <div class="result-title">五、无码盘盈设备（{{ (result.unlabeled || []).length }} 台，待盘点完成后确认处理）</div>
          <el-table :data="result.unlabeled" border size="mini" max-height="260">
            <el-table-column prop="device_name" label="名称" min-width="120" show-overflow-tooltip />
            <el-table-column prop="device_model" label="型号" min-width="100" show-overflow-tooltip />
            <el-table-column prop="location" label="位置" min-width="120" show-overflow-tooltip />
            <el-table-column label="照片" min-width="160" align="center">
              <template slot-scope="scope">
                <el-link v-if="scope.row.device_photo" type="primary" :href="'/media/' + scope.row.device_photo" target="_blank">设备照</el-link>
                <el-link v-if="scope.row.nameplate_photo" type="primary" :href="'/media/' + scope.row.nameplate_photo" target="_blank" style="margin-left:8px">铭牌照</el-link>
                <span v-if="!scope.row.device_photo && !scope.row.nameplate_photo">—</span>
              </template>
            </el-table-column>
            <el-table-column prop="note" label="备注" min-width="160" show-overflow-tooltip />
            <el-table-column label="操作" width="70" align="center" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" class="op-btn op-del" @click="handleDeleteDiff(scope.row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 六、本计划发现的其他科室设备 -->
        <div class="result-section">
          <div class="result-title">六、本计划发现的其他科室设备（{{ crossList.length }} 台）</div>
          <el-table :data="crossList" border size="mini" max-height="200">
            <el-table-column prop="equipment_id" label="设备ID" width="140" />
            <el-table-column prop="equipment_name" label="设备名称" min-width="160" show-overflow-tooltip />
            <el-table-column prop="true_department" label="真实科室" width="110" align="center" />
            <el-table-column label="发现时间" width="160" align="center">
              <template slot-scope="scope">{{ fmtTime(scope.row.found_at) }}</template>
            </el-table-column>
            <el-table-column label="是否闭环" width="90" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.resolved ? 'success' : 'info'" size="mini">
                  {{ scope.row.resolved ? '已闭环' : '未闭环' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="70" align="center" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" class="op-btn op-del" @click="handleDeleteDiff(scope.row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 七、科室签字 -->
        <div class="result-section">
          <div class="result-title">七、科室签字</div>
          <div v-if="signatureUrl">
            <img :src="signatureUrl" class="sign-preview" alt="科室签字" />
          </div>
          <div v-else class="section-empty">尚未签字（小程序结束盘点后可请科室人员签字，签字后此处显示并可随确认单 PDF 打印）</div>
        </div>
      </div>
      <div slot="footer">
        <el-button size="small" icon="el-icon-plus" type="warning" plain @click="openUnlabeled">登记无码设备</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="refreshResult">刷 新</el-button>
        <el-button size="small" type="primary" icon="el-icon-download" @click="downloadReport(resultRow)">
          下载确认单 PDF
        </el-button>
        <el-button size="small" @click="resultVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <!-- 无码设备手工登记 -->
    <el-dialog title="登记无码设备（盘盈）" :visible.sync="unlabeledVisible" width="540px" :close-on-click-modal="false">
      <el-form ref="unlabeledForm" :model="unlabeledForm" label-width="90px" size="small">
        <el-form-item label="设备名称">
          <el-input v-model="unlabeledForm.device_name" placeholder="看到但没贴码的设备名称（名称或位置至少填一项）" />
        </el-form-item>
        <el-form-item label="设备型号">
          <el-input v-model="unlabeledForm.device_model" placeholder="选填" />
        </el-form-item>
        <el-form-item label="发现位置">
          <el-input v-model="unlabeledForm.location" placeholder="如：三楼护士站（名称或位置至少填一项）" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="unlabeledForm.note" type="textarea" :rows="2" placeholder="选填，如「外观完好，待补录台账」" />
        </el-form-item>
        <el-form-item label="设备照片">
          <el-upload
            :auto-upload="false" :limit="1" list-type="picture" accept="image/*"
            :file-list="devicePhotoList" :on-change="(f, l) => devicePhotoList = l" :on-remove="(f, l) => devicePhotoList = l"
          >
            <el-button size="small">选择设备照片</el-button>
          </el-upload>
        </el-form-item>
        <el-form-item label="铭牌照片">
          <el-upload
            :auto-upload="false" :limit="1" list-type="picture" accept="image/*"
            :file-list="nameplatePhotoList" :on-change="(f, l) => nameplatePhotoList = l" :on-remove="(f, l) => nameplatePhotoList = l"
          >
            <el-button size="small">选择铭牌照片</el-button>
          </el-upload>
          <div class="create-tip">至少上传 1 张照片（设备照或铭牌照），最多各 1 张，便于事后追溯。</div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="unlabeledVisible = false">取 消</el-button>
        <el-button type="primary" size="small" :loading="unlabeledLoading" @click="submitUnlabeled">确 定登记</el-button>
      </div>
    </el-dialog>

    <!-- 设置令牌 -->
    <el-dialog title="设置小程序令牌" :visible.sync="tokenVisible" width="480px" :close-on-click-modal="false">
      <div class="token-tip">
        盘点接口支持两种鉴权：<b>① 后台账号登录</b>（推荐，登录后自动生效）；
        <b>② 小程序令牌</b>：打开 EMS 小程序 →「我的」页 → 复制令牌，粘贴到下方（与「用户管理」页共用）。
      </div>
      <el-input v-model="tokenInput" placeholder="粘贴 Token" />
      <div slot="footer">
        <el-button size="small" @click="tokenVisible = false">取 消</el-button>
        <el-button type="primary" size="small" @click="saveToken">保 存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '../api/inventory'

import DepartmentSelect from './DepartmentSelect.vue'

export default {
  name: 'InventoryList',
  components: { DepartmentSelect },
  data() {
    return {
      loading: false,
      plans: [],
      statusFilter: '',
      // 新建计划
      createVisible: false,
      creating: false,
      createForm: { name: '', department: '', planned_date: '' },
      createRules: {
        name: [{ required: true, message: '请输入计划名称', trigger: 'blur' }],
        department: [{ required: true, message: '请输入盘点科室', trigger: 'blur' }],
        planned_date: [{ required: true, message: '请选择计划日期', trigger: 'change' }],
      },
      // 盘点结果
      resultVisible: false,
      resultLoading: false,
      resultRow: {},
      result: {},
      crossList: [],
      // 无码设备登记
      unlabeledVisible: false,
      unlabeledLoading: false,
      unlabeledForm: { device_name: '', device_model: '', location: '', note: '' },
      devicePhotoList: [],
      nameplatePhotoList: [],
      // 令牌
      tokenVisible: false,
      tokenInput: '',
      // 科室签字
      signatureUrl: '',
    }
  },
  computed: {
    resultTitle() {
      return this.resultRow.name ? `盘点结果 — ${this.resultRow.name}` : '盘点结果'
    },
    // 未盘清单 + 跨科发现标注（真实科室=本计划科室 且 未闭环 → 标注"发现于X科"）
    missingWithCross() {
      const list = this.result.missing || []
      const crossMap = {}
      this.crossList.forEach(c => {
        if (!c.resolved && c.true_department === this.result.department) {
          crossMap[c.equipment_id] = c.found_in_department
        }
      })
      return list.map(m => ({
        ...m,
        found_in_department: crossMap[m.equipment_id] || '',
      }))
    },
  },
  created() {
    this.fetchPlans()
  },
  methods: {
    fetchPlans() {
      this.loading = true
      const params = this.statusFilter ? { status: this.statusFilter } : {}
      api.listPlans(params)
        .then(res => { this.plans = res.data || [] })
        .catch(() => {})
        .finally(() => { this.loading = false })
    },
    openCreate() {
      this.createForm = { name: '', department: '', planned_date: '' }
      this.createVisible = true
      this.$nextTick(() => this.$refs.createForm && this.$refs.createForm.clearValidate())
    },
    submitCreate() {
      this.$refs.createForm.validate(valid => {
        if (!valid) return
        this.creating = true
        api.createPlan(this.createForm)
          .then(() => {
            this.$message.success('盘点计划已创建，可在小程序端开始扫码盘点')
            this.createVisible = false
            this.fetchPlans()
          })
          .catch(err => {
            const detail = err.response && err.response.data && err.response.data.detail
            if (detail) this.$message.error(detail)
          })
          .finally(() => { this.creating = false })
      })
    },
    openResult(row) {
      this.resultRow = row
      this.resultVisible = true
      this.refreshResult()
    },
    refreshResult() {
      if (!this.resultRow.id) return
      this.resultLoading = true
      Promise.all([
        api.getPlanDetail(this.resultRow.id),
        api.getCrossDept(this.resultRow.id),
      ])
        .then(([detailRes, crossRes]) => {
          this.result = detailRes.data || {}
          this.crossList = crossRes.data || []
        })
        .catch(() => {})
        .finally(() => {
          this.resultLoading = false
          this.fetchSignature()
        })
    },
    fetchSignature() {
      if (!this.resultRow.id) return
      api.getSignature(this.resultRow.id)
        .then(res => {
          const url = res && res.data && res.data.signature_url
          // 签字图同名覆盖（signature.png），加时间戳破浏览器缓存，否则重签后仍显示旧图
          this.signatureUrl = url ? (url + '?t=' + Date.now()) : ''
        })
        .catch(() => { this.signatureUrl = '' })
    },
    completePlan(row) {
      this.$confirm(
        `确定结束盘点计划「${row.name}」吗？结束后小程序端将无法继续扫码（可重新开启）。`,
        '结束盘点',
        { type: 'warning' }
      ).then(() => api.completePlan(row.id))
        .then(() => {
          this.$message.success('盘点计划已结束')
          this.fetchPlans()
          if (this.resultVisible && this.resultRow.id === row.id) this.refreshResult()
        })
        .catch(err => {
          // 后端 400 会带具体原因（如"尚未签字确认"），必须透出，不能一律静默
          const d = err && err.response && err.response.data
          const msg = (d && (d.detail || d.message)) || '结束盘点失败'
          this.$message.error(msg)
        })
    },
    // 删除计划：级联影响面必须让用户看清（扫码记录 / 差异 / 无码照片一并删除）
    deletePlan(row) {
      const done = row.status === 'completed'
      const lines = [
        `科室：${row.department || '-'}`,
        `计划：${row.name || '-'}`,
        '',
        '将同时删除：该计划的全部扫码记录、全部差异记录（含跨科室/待核实/无码盘盈）以及无码登记的照片文件。',
      ]
      if (done) {
        lines.push('', '⚠ 该计划已「已完成」，删除后进度与确认单将一并消失，需重新盘点。')
      }
      this.$confirm(lines.join('\n'), '删除盘点计划', {
        type: 'warning',
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        dangerouslyUseHTMLString: false,
      }).then(() => api.deletePlan(row.id))
        .then(res => {
          const d = (res && res.data) || {}
          this.$message.success(
            `已删除（扫码 ${d.deleted_records || 0} 条 / 差异 ${d.deleted_differences || 0} 条 / 照片 ${d.deleted_photos || 0} 张）`
          )
          this.fetchPlans()
          if (this.resultVisible && this.resultRow.id === row.id) {
            this.resultVisible = false
            this.result = {}
            this.resultRow = {}
          }
        })
        .catch(err => {
          const d = err && err.response && err.response.data
          if (d && (d.detail || d.message)) {
            this.$message.error(d.detail || d.message)
          }
        })
    },
    reopenPlan(row) {
      this.$confirm(
        `确定重新开启盘点计划「${row.name}」吗？开启后小程序端可继续扫码。`,
        '重新开启',
        { type: 'info' }
      ).then(() => api.reopenPlan(row.id))
        .then(() => {
          this.$message.success('盘点计划已重新开启')
          this.fetchPlans()
          if (this.resultVisible && this.resultRow.id === row.id) this.refreshResult()
        })
        .catch(() => {})
    },
    downloadReport(row) {
      if (!row || !row.id) return
      api.downloadReport(row.id)
        .then(res => {
          const blob = new Blob([res.data], { type: 'application/pdf' })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `盘点确认单_${row.name || row.id}.pdf`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
        })
        .catch(() => {
          this.$message.error('确认单生成失败：服务端需安装 reportlab（pip install reportlab）')
        })
    },
    // 行内删除差异（待核实 / 跨科室 / 无码盘盈 三类通用）：差异 id = diff_id || id
    handleDeleteDiff(row) {
      const id = row.diff_id != null ? row.diff_id : row.id
      if (id == null) {
        this.$message.error('该行缺少差异ID，无法删除')
        return
      }
      this.$confirm('确认删除该差异记录？删除后将从盘点结果中移除。', '提示', { type: 'warning' })
        .then(() => api.deleteDifference(id))
        .then(() => {
          this.$message.success('已删除')
          this.refreshResult() // 重拉计划详情(pending/unlabeled) + 跨科列表，三块一并刷新
        })
        .catch((err) => {
          // $confirm 取消也会进 catch，无需提示；后端 closed 守卫 400 走 detail
          if (err && err.response && err.response.data && err.response.data.detail) {
            this.$message.error(err.response.data.detail)
          }
        })
    },
    // 无码设备登记
    openUnlabeled() {
      this.unlabeledForm = { device_name: '', device_model: '', location: '', note: '' }
      this.devicePhotoList = []
      this.nameplatePhotoList = []
      this.unlabeledVisible = true
      this.$nextTick(() => {
        if (this.$refs.unlabeledForm) this.$refs.unlabeledForm.clearValidate()
      })
    },
    submitUnlabeled() {
      if (!this.resultRow.id) return
      const name = (this.unlabeledForm.device_name || '').trim()
      const location = (this.unlabeledForm.location || '').trim()
      if (!name && !location) {
        this.$message.warning('请至少填写设备名称或发现位置')
        return
      }
      if (!this.devicePhotoList.length && !this.nameplatePhotoList.length) {
        this.$message.warning('请至少上传一张照片（设备照或铭牌照）')
        return
      }
      this.unlabeledLoading = true
      const fd = new FormData()
      fd.append('device_name', name)
      fd.append('device_model', (this.unlabeledForm.device_model || '').trim())
      fd.append('location', location)
      fd.append('note', (this.unlabeledForm.note || '').trim())
      if (this.devicePhotoList.length) fd.append('device_photo', this.devicePhotoList[0].raw)
      if (this.nameplatePhotoList.length) fd.append('nameplate_photo', this.nameplatePhotoList[0].raw)
      api.registerUnlabeled(this.resultRow.id, fd)
        .then(() => {
          this.$message.success('无码设备已登记为盘盈（待核实），可在「五、无码盘盈设备」查看')
          this.unlabeledVisible = false
          this.refreshResult()
        })
        .catch(err => {
          const detail = err.response && err.response.data && err.response.data.detail
          this.$message.error(detail || '登记失败')
        })
        .finally(() => { this.unlabeledLoading = false })
    },
    // 令牌
    openTokenInput() {
      this.tokenInput = ''
      this.tokenVisible = true
    },
    saveToken() {
      const t = (this.tokenInput || '').trim()
      if (!t) {
        this.$message.warning('请粘贴令牌')
        return
      }
      localStorage.setItem('ems_mp_admin_token', t)
      this.tokenVisible = false
      this.$message.success('令牌已保存')
      window.location.reload()
    },
    // 工具
    statusText(s) {
      return { ongoing: '进行中', paused: '暂停', completed: '已完成' }[s] || s
    },
    statusTag(s) {
      return { ongoing: '', paused: 'info', completed: 'success' }[s] || 'info'
    },
    percent(row) {
      if (!row.total_count) return 0
      const p = Math.round((row.scanned_count / row.total_count) * 100)
      return Math.min(100, Math.max(0, p))
    },
    fmtTime(v) {
      if (!v) return '-'
      return String(v).replace('T', ' ').slice(0, 16)
    },
  },
}
</script>

<style scoped>
.inventory-page {
  padding: 16px;
}
.box-card {
  /* 2026-10-21：铺满整窗 + 面板底色改豆沙绿（与全站护眼主题统一），不再限宽留白 */
  width: 100%;
  background-color: #f6f1e7;
  border: 1px solid #e3dcc8;
}
.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.page-title {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.section-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}
.progress-cell {
  display: flex;
  align-items: center;
}
.progress-num {
  font-weight: bold;
  color: #409eff;
  width: 56px;
  flex-shrink: 0;
}
.progress-done {
  color: #67c23a;
}
.progress-bar {
  flex: 1;
}
.op-btn {
  padding: 0;
  font-weight: bold;
}
.op-warn {
  color: #e6a23c;
}
.op-del {
  color: #f56c6c;
}
.unsigned-tag {
  margin-left: 4px;
}
.create-tip {
  margin-top: 4px;
  font-size: 12px;
  color: #e6a23c;
}
/* 结果弹窗 */
.stat-row {
  display: flex;
  justify-content: space-around;
  padding: 10px 0 14px;
  border-bottom: 1px dashed #ebeef5;
}
.stat-item {
  text-align: center;
}
.stat-num {
  font-size: 26px;
  font-weight: bold;
  color: #409eff;
}
.stat-danger {
  color: #f56c6c;
}
.stat-warn {
  color: #e6a23c;
}
.stat-label {
  font-size: 13px;
  color: #909399;
  margin-top: 2px;
}
.result-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin: 12px 0;
  font-size: 13px;
  color: #606266;
}
.result-meta .hl {
  color: #409eff;
}
.result-section {
  margin-top: 14px;
}
.result-title {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 8px;
}
.token-tip {
  font-size: 13px;
  color: #606266;
  margin-bottom: 12px;
  line-height: 1.7;
}
.sign-preview {
  max-width: 100%;
  max-height: 240px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
}
.section-empty {
  font-size: 13px;
  color: #909399;
  padding: 8px 0;
}
</style>
